import { AutomateDataState } from 'src/app/modules/admin/profile-management/profile-detail/automate-datastate-log/automate-datastate-model';
import { ProposalHistoryPagedData } from 'src/app/modules/admin/sale-account-management/sale-account.service';
import { Page } from 'src/app/shared/models/paging/page';
import { PagedData } from 'src/app/shared/models/paging/paged-data';
import { ReturnResult } from 'src/app/shared/models/return-result';
import { InquiryStageStat } from '../models/inquiry-stage-stat.model';

/**
 * ## Proposal History Mapper
 *
 * Transforms parallel repository responses (history page + stage list)
 * into a UI-safe shape consumed by the store.
 *
 * @responsibility sanitize incoming HTML-bearing strings
 * @responsibility normalize optional summary payloads
 */
export interface ProposalHistoryLoadMapped {
  pageData: PagedData<any>;
  total: number;
  stageStats: InquiryStageStat[];
  stageList: AutomateDataState[];
}

/** Removes HTML tags from plain string values used in labels/text fields. */
function stripHtmlTags(value: string): string {
  if (!value || typeof value !== 'string') {
    return value;
  }
  return value.replace(/<[^>]*>/g, '').trim();
}

/** Deep sanitizer for nested API payloads to keep UI text safe and readable. */
function sanitizeDeep(value: any): any {
  if (value == null) {
    return value;
  }
  if (typeof value === 'string') {
    return stripHtmlTags(value);
  }
  if (Array.isArray(value)) {
    return value.map(item => sanitizeDeep(item));
  }
  if (typeof value === 'object') {
    const next: any = {};
    Object.keys(value).forEach(key => {
      next[key] = sanitizeDeep(value[key]);
    });
    return next;
  }
  return value;
}

/**
 * Maps raw parallel-load response into normalized store payload.
 *
 * @param historyResp Paged history response with optional summary section.
 * @param stageResp Opportunity stage catalog response.
 * @param defaultPage Fallback page object used when history response is empty.
 */
export function mapProposalHistoryParallelLoad(
  historyResp: ReturnResult<ProposalHistoryPagedData<any>> | undefined,
  stageResp: ReturnResult<AutomateDataState[]> | undefined,
  defaultPage: Page,
): ProposalHistoryLoadMapped {
  const stageList = (stageResp?.result || []).map(item => ({
    ...item,
    dataStateName: stripHtmlTags(item?.dataStateName || ''),
  }));
  const result = historyResp?.result as ProposalHistoryPagedData<any>;
  if (!result) {
    return {
      pageData: { data: [], page: defaultPage } as PagedData<any>,
      total: 0,
      stageStats: [],
      stageList,
    };
  }

  const rawPageData = result as PagedData<any>;
  const rawItems = rawPageData?.data;
  const sanitizedItems = Array.isArray(rawItems)
    ? rawItems.map(item => (item == null ? item : sanitizeDeep(item)))
    : rawItems;

  const pageData = {
    ...rawPageData,
    data: sanitizedItems,
  } as PagedData<any>;
  const summary = result.summary;
  if (summary) {
    return {
      pageData,
      total: summary.total || 0,
      stageStats: (summary.stageStats || [])
        .map(item => ({ ...item, stageName: stripHtmlTags(item?.stageName || '') })),
      stageList,
    };
  }

  return {
    pageData,
    total: result?.page?.totalElements || 0,
    stageStats: [],
    stageList,
  };
}
